// EditorField — the ONE shared form-field component for the block-editor
// framework. ENG-C (Fable Hail Mary Wave 4), from WL-073's UI-consistency sweep
// (closes register rows UI-055/058/062/063/064/066 at the framework level).
//
// WHY THIS EXISTS
// Before ENG-C every editor hand-rolled its own field chrome: local `const INK/
// MUTED/PURPLE/BORDER` hexes duplicated across 6+ files, three different error
// reds, ad-hoc empty <ErrText>/<Typography> under each input, filled-vs-outlined
// height drift (48px vs 44px), and raw editorHeight literals (70px/120px). That
// is exactly the drift the audit flagged for the legacy editors (BusinessBox/
// GallerySettings/ProfileBox/LinkVault). This component is the single source of
// truth for a labelled field: one label + one input variant + one error + one
// helper + one counter, all sized/coloured from `tokens.field` — nothing hex,
// nothing per-editor. Every block editor composes THIS instead of re-styling.
//
// VARIANTS (`as`): "input" (default) · "textarea" (multiline; `minHeight` prop,
// no more 70px literals) · "select" (pass <option> children).
//
// STATES all handled here, from tokens: resting · focus (accent ring) · hover ·
// filled · disabled (bgDisabled + not-allowed) · invalid/error (red border +
// role=alert message). Accessibility: real <label htmlFor>, aria-invalid,
// aria-describedby wired to the error OR helper id, ≥44px target, visible
// focus-visible ring, reduced-motion safe.
//
// The styled field primitives (FieldLabel/HelpText/ErrorText/fieldFocusRing +
// the fieldChrome colour object) are exported so controls.jsx and the editors
// consume the SAME source instead of re-declaring constants (kills the G-07
// duplicate-shared-component drift).

import React, { useId } from "react";
import styled, { css } from "styled-components";
import { fontFamily, field, focusRing } from "../../../theme/tokens";

// Re-export the field palette so every sibling reads ONE source (no local consts).
export const fieldChrome = {
  ink: field.ink,
  muted: field.muted,
  accent: field.accent,
  border: field.border,
  borderHover: field.borderHover,
  borderInvalid: field.borderInvalid,
  error: field.errorText,
  placeholder: field.placeholder,
  bg: field.bg,
  bgDisabled: field.bgDisabled,
};

// Shared accent focus ring (reused by controls.jsx buttons/toggles/etc.).
export const fieldFocusRing = css`
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing.shadow};
  }
`;

export const FieldLabel = styled.label`
  display: block;
  font-family: ${fontFamily};
  font-size: ${field.labelSize}px;
  font-weight: 600;
  color: ${field.ink};
  margin-bottom: ${field.gap}px;
`;

export const HelpText = styled.div`
  font-family: ${fontFamily};
  font-size: ${field.helpSize}px;
  color: ${field.muted};
  line-height: 1.4;
  margin-top: 2px;
`;

export const ErrorText = styled.div`
  font-family: ${fontFamily};
  font-size: ${field.helpSize}px;
  font-weight: 500;
  color: ${field.errorText};
  line-height: 1.4;
  margin-top: ${field.gap}px;
`;

// Shared control chrome for input / textarea / select — identical resting,
// hover, focus, disabled + invalid treatment, so a text field, a multiline
// field and a dropdown never diverge again.
const controlChrome = css`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${(p) => (p.$invalid ? field.borderInvalid : field.border)};
  border-radius: ${field.radius}px;
  background: ${(p) => (p.disabled ? field.bgDisabled : field.bg)};
  font-family: ${fontFamily};
  font-size: 14px;
  color: ${field.ink};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &::placeholder { color: ${field.placeholder}; }
  &:hover:not(:disabled) { border-color: ${(p) => (p.$invalid ? field.borderInvalid : field.borderHover)}; }
  &:focus-visible {
    outline: none;
    border-color: ${field.accent};
    box-shadow: ${field.focusShadow};
  }
  &:disabled { cursor: not-allowed; color: ${field.muted}; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const StyledInput = styled.input`
  ${controlChrome};
  min-height: ${field.minHeight}px;
  padding: 0 12px;
`;

const StyledTextarea = styled.textarea`
  ${controlChrome};
  min-height: ${(p) => p.$minHeight || field.textareaMinHeight}px;
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
`;

const StyledSelect = styled.select`
  ${controlChrome};
  min-height: ${field.minHeight}px;
  padding: 0 10px;
  cursor: pointer;
`;

/**
 * EditorField — labelled form field with error/helper/counter, all from tokens.
 *
 * Props:
 *  - label       field label (rendered as a real <label htmlFor>)
 *  - value       controlled value
 *  - onChange    (nextValue) => void   — receives the raw string value
 *  - as          "input" | "textarea" | "select"   (default "input")
 *  - error       error message string (falsy = valid)
 *  - help        helper text (shown only when there is no error)
 *  - counterMax  when set, renders a right-aligned `len/max` counter
 *  - disabled    disabled state
 *  - minHeight   textarea min-height px (default tokens.field.textareaMinHeight)
 *  - children    <option> nodes when as="select"
 *  - id          optional explicit id (else auto)
 *  - gateTags    data-gate-tags string (defaults to the canonical field owner)
 *  - ...rest     forwarded to the underlying control (placeholder, rows, min…)
 */
const EditorField = ({
  label,
  value,
  onChange,
  as = "input",
  error,
  help,
  counterMax,
  disabled = false,
  minHeight,
  children,
  id,
  gateTags = "name:editor-field type:field owner:editor-field",
  ...rest
}) => {
  const autoId = useId();
  const fieldId = id || autoId;
  const errId = `${fieldId}-err`;
  const helpId = `${fieldId}-help`;
  const describedBy = error ? errId : help ? helpId : undefined;
  const emit = (e) => onChange && onChange(e.target.value);

  const common = {
    id: fieldId,
    disabled,
    $invalid: !!error,
    "aria-invalid": !!error,
    "aria-describedby": describedBy,
    onChange: emit,
    ...rest,
  };

  let control;
  if (as === "textarea") {
    control = <StyledTextarea {...common} value={value ?? ""} $minHeight={minHeight} />;
  } else if (as === "select") {
    control = (
      <StyledSelect {...common} value={value ?? ""}>
        {children}
      </StyledSelect>
    );
  } else {
    control = <StyledInput {...common} value={value ?? ""} />;
  }

  return (
    <div data-gate-tags={gateTags}>
      {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}
      {control}
      {counterMax != null && (
        <HelpText style={{ textAlign: "right" }}>
          {String(value || "").length}/{counterMax}
        </HelpText>
      )}
      {error ? (
        <ErrorText id={errId} role="alert">
          {error}
        </ErrorText>
      ) : help ? (
        <HelpText id={helpId}>{help}</HelpText>
      ) : null}
    </div>
  );
};

export default EditorField;
